import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import UsersList from "./UsersList";
import { getUsers } from "../../api/usersApi";
import { OrganizationResponseDto, Profile } from "../common/ObjectTypes";
import { getOrganizations } from "../../api/organizationsApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { OrganizationSearchQueryType } from "../common/QueryTypes";
import { isForbiddenError, pageSize } from "../../api/apiUtils";
import SideMenu from "../common/SideMenu";
import NoAccess from "../common/NoAccess";

type UserSearchQueryTypes = {
  skip: number;
  take: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  organization?: string | null;
  job_title?: string;
  is_admin?: boolean;
  is_active?: boolean;
  is_verified?: boolean;
  sort_by?: string;
  sort_direction?: string;
};

const ManageUsersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState<Profile[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [organizations, setOrganizations] = useState<OrganizationResponseDto[]>(
    [],
  );
  const [usersSkip, setUsersSkip] = useState(
    localStorage.getItem("usersTableSkip")
      ? parseInt(localStorage.getItem("usersTableSkip") as string)
      : 0,
  );
  const [userSearchQuery, setUserSearchQuery] = useState<UserSearchQueryTypes>({
    skip: localStorage.getItem("usersTableSkip")
      ? parseInt(localStorage.getItem("usersTableSkip") as string)
      : 0,
    take: pageSize,
    sort_by: "last_name",
    sort_direction: "asc",
    is_active: true,
  });
  const [organizationsQuery] = useState<OrganizationSearchQueryType>({
    skip: 0,
    take: pageSize,
  });
  const [pageLoaded, setPageLoaded] = useState(false);
  const [resultsLoaded, setResultsLoaded] = useState(false);
  // `GET /users` answers 403 for anyone who is not an admin. The page then shows the shared
  // no-access state in place of the list, with no toast (AKL-939) — the interceptor no
  // longer clears the token for a 403, so the user stays signed in.
  const [forbidden, setForbidden] = useState(false);

  const loadUsers = async (query: any) => {
    try {
      const temp = await getUsers(query);
      setUsers(temp.users);
      setTotalCount(temp.count);
      setPageLoaded(true);
      setResultsLoaded(true);
    } catch (err: any) {
      if (isForbiddenError(err)) {
        setForbidden(true);
        return;
      }
      toast.error(
        t("Failed to load users: ") + String(err?.data?.detail ?? ""),
      );
    }
  };

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const temp = await getOrganizations(organizationsQuery);
        setOrganizations(temp.organizations);
      } catch (err) {
        console.error("Error loading organizations:", err);
      }
    };

    loadOrganizations();
    loadUsers(userSearchQuery);
  }, [userSearchQuery.organization, userSearchQuery.is_active]);

  const handleAddUser = () => {
    navigate("/users/add");
  };

  const handleChangeSearchQuery = (e: any): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        if (e) {
          let name: string;
          let value: string | boolean;
          if (e.target.value === "is_active") {
            name = e.target.value;
            value = !e.target.checked;
          } else {
            name = e.target.name;
            value = e.target.value;
          }
          setUserSearchQuery((prevQuery) => ({
            ...prevQuery,
            [name]: value,
          }));
        } else {
          setUserSearchQuery((prevQuery) => ({
            ...prevQuery,
            organization: null,
          }));
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleSearch = async () => {
    let query = {
      ...userSearchQuery,
      skip: 0,
    };
    setUserSearchQuery((prevQuery) => ({
      ...prevQuery,
      skip: 0,
    }));
    localStorage.setItem("usersTableSkip", "0");
    setUsersSkip(0);
    setResultsLoaded(false);
    await loadUsers(query);
  };

  const handleSelectUser = (user_id: string) => {
    navigate("/users/" + user_id);
  };

  const requestSort = async (sort_column: string, direction: boolean) => {
    let query = {
      ...userSearchQuery,
      sort_by: sort_column,
      sort_direction: direction ? "asc" : "desc",
    };
    setResultsLoaded(false);
    setUserSearchQuery((prevQuery) => ({
      ...prevQuery,
      sort_by: sort_column,
      sort_direction: direction ? "asc" : "desc",
    }));
    await loadUsers(query);
  };

  const handlePageClick = async (e: any) => {
    let query = {
      ...userSearchQuery,
      skip: e.selected * pageSize,
    };
    setResultsLoaded(false);
    setUserSearchQuery((prevQuery) => ({
      ...prevQuery,
      skip: e.selected * pageSize,
    }));
    setUsersSkip(e.selected * pageSize);
    localStorage.setItem("usersTableSkip", (e.selected * pageSize).toString());
    await loadUsers(query);
  };

  if (forbidden) {
    // The same shell UsersList renders (side menu beside the content), so the page still
    // reads as part of the app; only the list is replaced.
    return (
      <div className="flex flex-row w-full">
        <SideMenu />
        <div className="w-full mx-8">
          <NoAccess />
        </div>
      </div>
    );
  }

  return (
    <div>
      <UsersList
        users={users}
        take={userSearchQuery.take}
        offset={usersSkip}
        totalCount={totalCount}
        onPageClicked={handlePageClick}
        organizations={organizations}
        userSearchQuery={userSearchQuery}
        onChangeSearchQuery={handleChangeSearchQuery}
        onSearch={handleSearch}
        onSelectUser={handleSelectUser}
        onClickAdd={handleAddUser}
        requestSort={requestSort}
        loading={!pageLoaded}
        resultsLoaded={resultsLoaded}
      />
    </div>
  );
};

export default ManageUsersPage;
